import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Check, Eye, FileText, Pencil, GripVertical,
  MoreVertical, Download, ArrowRight, Copy, Trash2, Lock,
  Info, EyeOff, Edit2, FolderSymlink, Palette, Hash, Clock, ClockAlert, Gift,
} from 'lucide-react';
import { RyanConfig } from '../types/ryan.types';
import { getRyanSubdeckColor } from '../constants/ryanFieldVisuals';
import { getRyanMedicalIcon } from '../constants/ryanMedicalIcons';
import RyanCompraModal from './RyanCompraModal';
import './RyanSubdeckCard.css';

// Tooltip via portal (document.body) em vez de position:absolute preso ao
// próprio card — um subdeck no meio do grid tinha a descrição/"Gratuito"
// cortada pelo card vizinho (que vem depois no DOM e pinta por cima), já que
// position:absolute só escapa do PAI, nunca dos irmãos. Portal joga o
// tooltip pra fora de qualquer card, então nada mais o corta. Mesmo padrão
// já usado pelo menu kebab logo abaixo (getBoundingClientRect + createPortal).
function useHoverTooltip<T extends HTMLElement>(tooltipWidth = 200) {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, centerX: 0 });
  const ref = useRef<T>(null);
  const show = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const margin = 8;
      // Trava dentro da tela — sem isso, um card na última coluna do grid
      // mandava a tooltip de 200px pra fora da viewport pela direita.
      const left = Math.min(
        rect.left + window.scrollX,
        window.scrollX + window.innerWidth - tooltipWidth - margin
      );
      setPos({
        top: rect.bottom + window.scrollY + 6,
        left,
        centerX: rect.left + window.scrollX + rect.width / 2,
      });
    }
    setIsOpen(true);
  };
  const hide = () => setIsOpen(false);
  return { ref, isOpen, pos, show, hide };
}

interface SubdeckProp {
  _id: string;
  name: string;
  description?: string;
  type: 'flashcards' | 'apostila';
  hasResumo?: boolean;
  cardCount: number;
  deckId: string;
  masteryPercentage: number | null;
  hasRead?: boolean;
  readPercentage?: number | null;
  isBlocked?: boolean;
  isHidden?: boolean;
  excludeFromCount?: boolean;
  // "Restringir temporariamente" — estado INDEPENDENTE de isBlocked (ver
  // comentário em models/RyanSpace.js). Admin sempre acessa; usuário comum é
  // bloqueado (real, no backend) e vê restrictedMessage em vez do padrão de
  // interdição.
  restricted?: boolean;
  restrictedMessage?: string;
  // "Deixar gratuito" — libera este subdeck pra qualquer conta logada,
  // ignorando compra/grant (sem prazo, até o admin desmarcar). Ver
  // hasRyanApostilaAccess em server/middleware/ryanAccessControl.js.
  isFree?: boolean;
  pageCount?: number;
  iconId?: string | null;
  badge?: string | null;
  // Link da prévia ativa (/ryan/previa-cards/... ou /previa/...) — vira o selo
  // "Prévia" pra quem não tem acesso, abrindo a prévia dentro da plataforma
  previewUrl?: string;
}

interface RyanSubdeckCardProps {
  subdeck: SubdeckProp;
  spaceId: string;
  spaceName?: string;
  isAdmin?: boolean;
  isVip?: boolean;
  isEditor?: boolean;
  comprado?: boolean;
  ryanConfig?: RyanConfig;
  dragHandleAttrs?: { attributes: any; listeners: any };
  onDelete?: () => void;
  onExport?: () => void;
  onExportApostila?: () => void;
  onMove?: () => void;
  onCopy?: () => void;
  onMarkRead?: () => void;
  onToggleBlock?: () => void;
  onToggleHidden?: () => void;
  onToggleExcludeCount?: () => void;
  onToggleFree?: () => void;
  /** Abre o modal de configuração de "Restringir temporariamente" (novo, ou editando a mensagem já ativa). */
  onOpenRestrict?: () => void;
  /** Remove a restrição temporária ativa — volta ao normal imediatamente. */
  onRemoveRestriction?: () => void;
  onEdit?: () => void;
  onBlockedClick?: () => void;
  /** Clique de usuário comum num subdeck restricted (não isBlocked) — abre o modal com a mensagem do admin. */
  onRestrictedClick?: () => void;
  onAssignGroup?: () => void;
  onAssignCategory?: () => void;
  onConfigurePreview?: () => void;
  onConfigureApostilaPreview?: () => void;
  /** Cor sólida do card — vem da cor do próprio subdeck (grid/groups) ou da categoria (modo "Por Tipo") */
  accentColor?: string;
  /** Modo de seleção múltipla (mover subdecks entre abas em lote) */
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}

const RyanSubdeckCard: React.FC<RyanSubdeckCardProps> = ({
  subdeck, spaceId, spaceName = '', isAdmin, isVip = false, isEditor = false, comprado = false, ryanConfig,
  dragHandleAttrs,
  onDelete, onExport,
  onExportApostila,
  onMove, onCopy,
  onMarkRead, onToggleBlock, onToggleHidden, onToggleExcludeCount, onToggleFree, onOpenRestrict, onRemoveRestriction,
  onEdit, onBlockedClick, onRestrictedClick, onAssignGroup, onAssignCategory,
  onConfigurePreview, onConfigureApostilaPreview,
  accentColor, selectionMode = false, isSelected = false, onToggleSelect,
}) => {
  const navigate = useNavigate();
  const returnPath = `/ryan/campo/${spaceId}`;
  const state = { isPurchasedDeck: true, fromRyan: true, returnPath, spaceName, spaceId };

  const [showKebab, setShowKebab] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [showCompraModal, setShowCompraModal] = useState(false);

  const kebabBtnRef = useRef<HTMLButtonElement>(null);
  const kebabMenuRef = useRef<HTMLDivElement>(null);

  const freeTooltip = useHoverTooltip<HTMLSpanElement>(90);
  const descTooltip = useHoverTooltip<HTMLSpanElement>(200);

  const blocked = subdeck.isBlocked && !isAdmin;
  // "Restringir temporariamente" — estado próprio, checado à parte de
  // isBlocked (a ordem entre os dois não importa pra UX: qualquer um dos
  // dois já impede o acesso de usuário comum; o gate de verdade, que não dá
  // pra contornar por URL direta, é no backend — ver getRyanRestriction).
  const restricted = subdeck.restricted && !isAdmin;
  // isVip/isEditor NÃO entram mais aqui de propósito — bypass total virou
  // seleção curada por conta (RyanAccess.curatedAccessEnabled), resolvida no
  // servidor (GET /spaces, GET /spaces/:spaceId) e já refletida em `comprado`
  // antes de chegar aqui. Checar isVip/isEditor de novo no client seria
  // reimplementar essa regra pela segunda vez, sem saber se a conta tem
  // seleção curada ativa — exatamente a duplicação que permitia ver
  // conteúdo fora da seleção antes desta mudança.
  const podeAcessar = isAdmin || comprado;

  const guard = (fn: () => void) => () => {
    if (blocked) { onBlockedClick?.(); return; }
    if (restricted) { onRestrictedClick?.(); return; }
    if (!podeAcessar) { setShowCompraModal(true); return; }
    fn();
  };

  const apostilaState = {
    ...state,
    isRyanEditor: isEditor && !isAdmin,
  };

  const goStudy = guard(() => {
    sessionStorage.setItem('graia_theme_context', 'ryan');
    navigate(`/study/${subdeck.deckId}/${subdeck._id}`, { state });
  });
  const goView  = guard(() => {
    sessionStorage.setItem('graia_theme_context', 'ryan');
    navigate(`/view/${subdeck.deckId}/${subdeck._id}`, { state });
  });
  const goApostila = guard(() => navigate(`/resumo/${subdeck.deckId}/${subdeck._id}`, { state: apostilaState }));

  // Selo "Prévia": só pra quem não tem acesso (e o subdeck não está interditado/
  // restrito/gratuito). Abre a prévia aqui mesmo, com "voltar" pro campo.
  const showPreviewBadge = !!subdeck.previewUrl && !podeAcessar && !blocked && !restricted && !subdeck.isFree;
  const goPreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!subdeck.previewUrl) return;
    sessionStorage.setItem('graia_theme_context', 'ryan');
    navigate(subdeck.previewUrl, { state: { fromRyan: true, returnPath } });
  };
  const goEdit  = guard(() => {
    sessionStorage.setItem('graia_theme_context', 'ryan');
    navigate(`/editor/${subdeck.deckId}/${subdeck._id}`, { state });
  });

  const mastery = subdeck.masteryPercentage ?? 0;
  // "Marcar como lida" é um override manual: com hasRead ativo, a barra vai
  // pra 100% na hora (sem esperar reload); ao desmarcar, volta a refletir o
  // readPercentage real por página (progresso do ResumoProgress), não zera.
  const readPct = subdeck.hasRead ? 100 : (subdeck.readPercentage ?? 0);
  const resolvedColor = accentColor || getRyanSubdeckColor();
  const IconComp = getRyanMedicalIcon(subdeck.iconId) || (subdeck.type === 'apostila' ? FileText : BookOpen);
  const segments = [0, 1, 2, 3, 4].map(i => {
    const value = subdeck.type === 'apostila' ? readPct : mastery;
    return Math.min(100, Math.max(0, (value - i * 20) / 20 * 100));
  });

  const openKebab = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (kebabBtnRef.current) {
      const rect = kebabBtnRef.current.getBoundingClientRect();
      const menuWidth = 340; // precisa bater com .rsc-kebab-menu (width) no CSS
      const margin = 8;
      const left = Math.min(
        rect.right + window.scrollX - menuWidth,
        window.scrollX + window.innerWidth - menuWidth - margin
      );
      setMenuPos({
        top: rect.bottom + window.scrollY + 4,
        left: Math.max(window.scrollX + margin, left),
      });
    }
    setShowKebab(v => !v);
    setDeleteConfirm(false);
  };

  const closeKebab = () => {
    setShowKebab(false);
    setDeleteConfirm(false);
  };

  useEffect(() => {
    if (!showKebab) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        kebabBtnRef.current?.contains(target) ||
        kebabMenuRef.current?.contains(target)
      ) return;
      closeKebab();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showKebab]);

  const kebabMenu = showKebab
    ? ReactDOM.createPortal(
        <div
          ref={kebabMenuRef}
          className="rsc-kebab-menu"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          {subdeck.type === 'flashcards' ? (
            <>
              <button className="rsc-kebab-item" onClick={() => { closeKebab(); onExport?.(); }}>
                <Download size={13} /> Exportar cards
              </button>
              <button className="rsc-kebab-item" onClick={() => { closeKebab(); onMove?.(); }}>
                <ArrowRight size={13} /> Mover
              </button>
              <button className="rsc-kebab-item" onClick={() => { closeKebab(); onCopy?.(); }}>
                <Copy size={13} /> Copiar
              </button>
            </>
          ) : (
            <button className="rsc-kebab-item rsc-kebab-item--wide" onClick={() => { closeKebab(); onExportApostila?.(); }}>
              <Download size={13} /> Exportar apostila
            </button>
          )}
          {isAdmin && (
            <>
              <div className="rsc-kebab-divider" />
              <button className="rsc-kebab-item" onClick={() => { closeKebab(); onEdit?.(); }}>
                <Edit2 size={13} /> Editar
              </button>
              {onAssignGroup && (
                <button className="rsc-kebab-item" onClick={() => { closeKebab(); onAssignGroup(); }}>
                  <FolderSymlink size={13} /> Mover para grupo
                </button>
              )}
              {onAssignCategory && (
                <button className="rsc-kebab-item" onClick={() => { closeKebab(); onAssignCategory(); }}>
                  <Palette size={13} /> Categoria
                </button>
              )}
              {subdeck.type === 'flashcards' && onConfigurePreview && (
                <button className="rsc-kebab-item" onClick={() => { closeKebab(); onConfigurePreview(); }}>
                  <Eye size={13} /> Configurar prévia
                </button>
              )}
              {subdeck.type === 'apostila' && onConfigureApostilaPreview && (
                <button className="rsc-kebab-item" onClick={() => { closeKebab(); onConfigureApostilaPreview(); }}>
                  <Eye size={13} /> Configurar prévia
                </button>
              )}
              <button className="rsc-kebab-item" onClick={() => { closeKebab(); onToggleBlock?.(); }}>
                <Lock size={13} />
                {subdeck.isBlocked ? 'Remover interdição' : 'Interditar'}
              </button>
              <button
                className="rsc-kebab-item"
                onClick={() => { closeKebab(); subdeck.restricted ? onRemoveRestriction?.() : onOpenRestrict?.(); }}
                title="Bloqueia o acesso de usuários comuns com uma mensagem sua — admin continua acessando normalmente"
              >
                {subdeck.restricted ? <><ClockAlert size={13} /> Remover restrição</> : <><Clock size={13} /> Restringir temporariamente</>}
              </button>
              <button
                className="rsc-kebab-item"
                onClick={() => { closeKebab(); onToggleFree?.(); }}
                title="Libera este deck pra qualquer conta logada, sem precisar de compra — sem prazo, até você desmarcar"
              >
                <Gift size={13} />
                {subdeck.isFree ? 'Remover gratuito' : 'Deixar gratuito'}
              </button>
              <button className="rsc-kebab-item" onClick={() => { closeKebab(); onToggleHidden?.(); }}>
                {subdeck.isHidden ? <><Eye size={13} /> Mostrar</> : <><EyeOff size={13} /> Esconder</>}
              </button>
              {subdeck.type === 'flashcards' && (
                <button
                  className="rsc-kebab-item"
                  onClick={() => { closeKebab(); onToggleExcludeCount?.(); }}
                  title="Pra decks de revisão com cards reaproveitados de outros decks — não conta como card novo no total do campo"
                >
                  <Hash size={13} />
                  {subdeck.excludeFromCount ? 'Contar no header' : 'Não contar no header'}
                </button>
              )}
            </>
          )}
          <div className="rsc-kebab-divider" />
          {!deleteConfirm ? (
            <button
              className="rsc-kebab-item rsc-kebab-item--danger"
              onClick={() => setDeleteConfirm(true)}
            >
              <Trash2 size={13} /> Excluir
            </button>
          ) : (
            <div className="rsc-kebab-confirm">
              <span className="rsc-kebab-confirm-text">Tem certeza?</span>
              <button className="rsc-kebab-confirm-yes" onClick={() => { closeKebab(); onDelete?.(); }}>
                Sim
              </button>
              <button className="rsc-kebab-confirm-no" onClick={() => setDeleteConfirm(false)}>
                Não
              </button>
            </div>
          )}
        </div>,
        document.body
      )
    : null;

  const cardStyle = { '--rsc-accent': resolvedColor } as React.CSSProperties;

  return (
    <div
      className={`rsc-card${subdeck.isBlocked ? ' rsc-card--blocked' : ''}${subdeck.restricted ? ' rsc-card--restricted' : ''}${subdeck.isHidden ? ' rsc-card--hidden' : ''}${selectionMode ? ' rsc-card--selectable' : ''}${isSelected ? ' rsc-card--selected' : ''}`}
      style={cardStyle}
      onClick={selectionMode ? () => onToggleSelect?.() : undefined}
    >
      <div className="rsc-accent-bar" style={{ background: resolvedColor }} />

      {/* Header row: ícone | name | info tooltip | badge oculto | grip | kebab | checkbox de seleção */}
      <div className="rsc-card-header">
        <div className="rsc-icon-badge">
          <IconComp size={17} />
        </div>
        <h3 className="rsc-name">{subdeck.name}</h3>
        {isAdmin && subdeck.isHidden && (
          <span className="rsc-hidden-badge">Oculto</span>
        )}

        {subdeck.isFree && (
          <span
            ref={freeTooltip.ref}
            className="rsc-free-badge"
            style={{ background: resolvedColor }}
            onMouseEnter={freeTooltip.show}
            onMouseLeave={freeTooltip.hide}
          >
            <Gift size={11} />
          </span>
        )}

        {showPreviewBadge && (
          <button
            type="button"
            className="rsc-preview-badge"
            style={{ background: resolvedColor }}
            onClick={goPreview}
            title="Ver a prévia deste material"
          >
            <Eye size={11} />
            Prévia
          </button>
        )}

        {subdeck.description && (
          <span
            ref={descTooltip.ref}
            className="rsc-info-btn"
            onMouseEnter={descTooltip.show}
            onMouseLeave={descTooltip.hide}
          >
            <Info size={14} />
          </span>
        )}

        {!selectionMode && isAdmin && dragHandleAttrs && (
          <div
            className="rsc-drag-handle"
            {...dragHandleAttrs.attributes}
            {...dragHandleAttrs.listeners}
            title="Arrastar para reordenar"
          >
            <GripVertical size={16} />
          </div>
        )}

        {!selectionMode && isAdmin && (
          <button
            ref={kebabBtnRef}
            className="rsc-kebab-btn"
            onClick={openKebab}
            title="Ações"
          >
            <MoreVertical size={16} />
          </button>
        )}

        {selectionMode && (
          <input
            type="checkbox"
            className="rsc-select-checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect?.()}
            onClick={e => e.stopPropagation()}
            title={isSelected ? 'Desmarcar' : 'Selecionar'}
          />
        )}
      </div>

      {subdeck.isBlocked && (
        <div className="rsc-blocked-banner">
          <Lock size={11} />
          Em obras
        </div>
      )}

      {subdeck.restricted && (
        <div className="rsc-restricted-banner" title={subdeck.restrictedMessage || undefined}>
          <Clock size={11} />
          Indisponível temporariamente
        </div>
      )}

      {subdeck.type === 'apostila' && !subdeck.hasResumo ? (
        <div className="rsc-stats rsc-stats--empty">
          <span className="rsc-empty-text">Sem conteúdo ainda</span>
        </div>
      ) : (
        <div className="rsc-stats">
          <div className="rsc-stats-row">
            <div className="rsc-stat-left">
              {!subdeck.isBlocked && !subdeck.restricted && (
                subdeck.type === 'apostila' ? (
                  subdeck.pageCount !== undefined && subdeck.pageCount > 0 && (
                    <span className="rsc-stat-count">{subdeck.pageCount} págs.</span>
                  )
                ) : (
                  <span className="rsc-stat-count">{subdeck.cardCount} cards</span>
                )
              )}
              {subdeck.badge && <span className="rsc-stat-badge">{subdeck.badge}</span>}
            </div>
            {subdeck.type === 'apostila' && subdeck.hasResumo && (
              <span className="rsc-read-check-wrap">
                <button
                  type="button"
                  className={`rsc-read-check${subdeck.hasRead ? ' rsc-read-check--active' : ''}`}
                  style={subdeck.hasRead ? { color: resolvedColor, borderColor: resolvedColor } : undefined}
                  onClick={e => { e.stopPropagation(); onMarkRead?.(); }}
                >
                  <Check size={13} />
                </button>
                <span className="rsc-read-check-tooltip" style={{ background: resolvedColor }}>
                  {subdeck.hasRead ? 'Lida — clique para desmarcar' : 'Marcar como lida'}
                </span>
              </span>
            )}
          </div>
          <div className="rsc-mastery">
            <div className="rsc-mastery-row">
              <span className="rsc-mastery-label">{subdeck.type === 'apostila' ? 'Leitura' : 'Domínio'}</span>
              <span className="rsc-mastery-pct">{subdeck.type === 'apostila' ? readPct : mastery}%</span>
            </div>
            <div className="rsc-segs">
              {segments.map((fill, i) => (
                <div key={i} className="rsc-seg">
                  <div className="rsc-seg-fill" style={{ width: `${fill}%` }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!selectionMode && (
        <div className="rsc-actions">
          {subdeck.type === 'apostila' ? (
            <button className="rsc-btn-primary" onClick={goApostila}>
              <FileText size={14} />
              Acessar Apostila
            </button>
          ) : (
            <>
              <button className="rsc-btn-primary" onClick={goStudy}>
                <BookOpen size={14} />
                Estudar
              </button>
              <button className="rsc-btn-secondary rsc-btn-icon" onClick={goView} title="Ver cards">
                <Eye size={15} />
              </button>
              {(isAdmin || isEditor) && (
                <button className="rsc-btn-secondary rsc-btn-icon" onClick={goEdit} title="Editar cards">
                  <Pencil size={15} />
                </button>
              )}
            </>
          )}
        </div>
      )}

      {kebabMenu}

      {freeTooltip.isOpen && subdeck.isFree && ReactDOM.createPortal(
        <span
          className="rsc-free-tooltip-portal"
          style={{ top: freeTooltip.pos.top, left: freeTooltip.pos.centerX, background: resolvedColor }}
        >
          Gratuito
        </span>,
        document.body
      )}

      {descTooltip.isOpen && subdeck.description && ReactDOM.createPortal(
        <span
          className="rsc-tooltip-portal"
          style={{ top: descTooltip.pos.top, left: descTooltip.pos.left }}
        >
          {subdeck.description}
        </span>,
        document.body
      )}

      {showCompraModal && ryanConfig && (
        <RyanCompraModal
          isOpen={showCompraModal}
          onClose={() => setShowCompraModal(false)}
          spaceId={spaceId}
          spaceName={spaceName}
          ryanConfig={ryanConfig}
        />
      )}
    </div>
  );
};

export default RyanSubdeckCard;
